import { useMemo } from "react";

import { Models } from "@/api/types";
import { CatalogService as CoreCatalogService } from "@/services/CatalogService";
import { OrderHistoryData } from "@/services/CatalogTypes";
import { get } from "api";

export const useCatalogService = (): CoreCatalogService => {
  return useMemo(() => {
    const provider = {
      getListing: async (): Promise<Models<"ListingModel">[]> => {
        const listings = (await get("/listings")) as
          | Models<"ListingModel">[]
          | undefined;
        return listings ?? [];
      },
      getListingById: async (id: string): Promise<Models<"ListingModel">> => {
        const listing = (await get("/listings/{id}", {
          id,
        })) as Models<"ListingModel"> | undefined;
        if (!listing) {
          throw new Error(`Listing with id ${id} not found`);
        }
        return listing;
      },
      getListingPackages: async (
        id: string
      ): Promise<Models<"PackageModel">[]> => {
        const packages = (await get("/listings/{id}/packages", {
          id,
        })) as Models<"PackageModel">[] | undefined;
        return packages ?? [];
      },
      getListingExpectations: async (
        id: string
      ): Promise<Models<"PackageListingExpectationModel">[]> => {
        const expectations = (await get("/listings/{id}/expectations", {
          id,
        })) as Models<"PackageListingExpectationModel">[] | undefined;
        return expectations ?? [];
      },
      getMemberships: async (): Promise<Models<"MembershipModel">[]> => {
        const memberships = (await get("/memberships")) as
          | Models<"MembershipModel">[]
          | undefined;
        return memberships ?? [];
      },
      getMembershipDetails: async (
        id: string
      ): Promise<Models<"MembershipModel">> => {
        const membership = (await get("/memberships/{membershipId}", {
          membershipId: id,
        })) as Models<"MembershipModel"> | undefined;
        if (!membership) {
          throw new Error(`Membership with id ${id} not found`);
        }
        return membership;
      },
      getMembershipRewards: async (
        id: string
      ): Promise<Models<"MembershipRewardGroupModel">[]> => {
        const rewards = (await get("/memberships/{membershipId}/rewards", {
          membershipId: id,
        })) as Models<"MembershipRewardGroupModel">[] | undefined;
        return rewards ?? [];
      },
      getShopData: async () => {
        const shopData = (await get("/shop")) as
          | {
              concerns?: Array<{ id: string; name?: string | null }>;
              areas?: Array<{ id: string; name?: string | null }>;
            }
          | undefined;
        return shopData ?? { concerns: [], areas: [] };
      },
      getShopDiscounts: async (): Promise<Models<"ShopDiscountModel">> => {
        const discounts = (await get("/shop/discounts")) as
          | Models<"ShopDiscountModel">
          | undefined;
        return (
          discounts ??
          ({
            listingDiscounts: {},
            membershipDiscounts: {},
          } as Models<"ShopDiscountModel">)
        );
      },
      getListingShopDiscounts: async (
        listingId: string
      ): Promise<Models<"ListingShopDiscountModel">> => {
        const detailed = (await get("/shop/discounts/listings/{listingId}", {
          listingId,
        })) as Models<"ListingShopDiscountModel"> | undefined;
        return (
          detailed ??
          ({
            packageDiscounts: {},
          } as unknown as Models<"ListingShopDiscountModel">)
        );
      },
      getPromotions: async (): Promise<Models<"PromotionModel">[]> => {
        const promotions = (await get("/promotions")) as
          | Models<"PromotionModel">[]
          | undefined;
        return promotions ?? [];
      },
      getOrderHistory: async (page = 1): Promise<OrderHistoryData> => {
        const result = (await get("/user/orders", { page })) as {
          items?: Models<"OrderHistoryLineItemModel">[];
          page?: number;
          totalPages?: number;
          count?: number;
        } | null;
        return {
          orders: result?.items ?? [],
          page: result?.page ?? 1,
          totalPages: result?.totalPages ?? 1,
          count: result?.count ?? 0,
        };
      },
      getOrderDetails: async (
        orderId: string
      ): Promise<Models<"CartModel">> => {
        const result = (await get("/user/orders/{shopDisplayOrderId}", {
          shopDisplayOrderId: orderId,
        })) as Models<"CartModel"> | undefined;
        if (!result) {
          throw new Error(`Order with id ${orderId} not found`);
        }
        return result;
      },
      getPackageById: async (
        packageId: string
      ): Promise<Models<"PackageModel">> => {
        const result = (await get("/packages/{packageId}", {
          packageId,
        })) as Models<"PackageModel"> | undefined;
        if (!result) {
          throw new Error(`Package with id ${packageId} not found`);
        }
        return result;
      },
    };

    return new CoreCatalogService(provider);
  }, []);
};
